import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import '../../../shared/mockups/mockups.css';
import './Mockups.css';
import { exportImagePng, exportVideo } from './mockupExport';

type Kind = 'chrome' | 'safari' | 'iphone' | 'pixel';
const IS_BROWSER = (t: Kind) => t === 'chrome' || t === 'safari';

const COLORS: Record<string, { id: string; label: string }[]> = {
  iphone: [
    { id: '', label: 'Negro' },
    { id: 'is-silver', label: 'Plata' },
    { id: 'is-gold', label: 'Oro' },
    { id: 'is-deep-purple', label: 'Morado' },
  ],
  pixel: [
    { id: '', label: 'Obsidiana' },
    { id: 'is-snow', label: 'Nieve' },
    { id: 'is-hazel', label: 'Verde' },
  ],
};

const ASPECTS = ['16 / 10', '16 / 9', '3 / 2', '4 / 3', 'auto'];
const ASPECT_LABEL: Record<string, string> = {
  '16 / 10': '16:10',
  '16 / 9': '16:9',
  '3 / 2': '3:2',
  '4 / 3': '4:3',
  auto: 'Auto',
};

const PLACEHOLDER =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='1200' height='834'><rect width='100%' height='100%' fill='#e9edfb'/><text x='50%' y='50%' font-family='monospace' font-size='34' fill='#9aa2c8' text-anchor='middle' dominant-baseline='middle'>captura</text></svg>`,
  );

const clamp = (v: number) => Math.max(0, Math.min(100, v));

function downloadBlob(blob: Blob, name: string) {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  URL.revokeObjectURL(a.href);
}

export function Mockups() {
  const [type, setType] = useState<Kind>('chrome');
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const [color, setColor] = useState('');
  const [url, setUrl] = useState('pisoadvisor.app');
  const [aspect, setAspect] = useState('16 / 10');
  const [width, setWidth] = useState(1100);
  const [img, setImg] = useState<string | null>(null);
  const [status, setStatus] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [mediaKind, setMediaKind] = useState<'image' | 'video'>('image');
  const [bg, setBg] = useState('#ffffff');
  const fileRef = useRef<File | null>(null);
  const objectUrlRef = useRef<string | null>(null);

  const hostRef = useRef<HTMLDivElement>(null);
  const scaleRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const dimRef = useRef({ w: 0, h: 0 }); // dimensiones naturales de la imagen
  const posRef = useRef({ x: 50, y: 0 }); // object-position (%)
  const previewScale = useRef(1);
  const drag = useRef<null | { x: number; y: number; px: number; py: number; ovx: number; ovy: number; img: HTMLImageElement | HTMLVideoElement }>(null);

  function buildMarkup(): string {
    const src = img || PLACEHOLDER;
    const pos = `object-position:${posRef.current.x}% ${posRef.current.y}%`;
    let media = `<img src="${src}" style="${pos}" alt="">`;
    if (mediaKind === 'video') {
      media = `<video src="${src}" style="${pos}" autoplay muted loop playsinline></video>`;
    }
    if (IS_BROWSER(type)) {
      const ar =
        aspect === 'auto'
          ? dimRef.current.w && dimRef.current.h
            ? `${dimRef.current.w} / ${dimRef.current.h}`
            : '16 / 10'
          : aspect;
      return `
      <div class="mockup-export"><div class="browser-frame" style="width:${width}px">
        <div class="browser-ui" aria-hidden="true">
          <div class="chrome-ui">
            <div class="top"><span class="mk mk-tab"></span></div>
            <div class="bottom">
              <span class="mk mk-nav"></span>
              <div class="address-bar">
                <svg class="lock" viewBox="0 0 24 24"><path d="M6 10V8a6 6 0 0 1 12 0v2"/><rect x="4" y="10" width="16" height="11" rx="2"/></svg>
                <p>${url}</p>
              </div>
              <span class="mk mk-menu"></span>
            </div>
          </div>
          <div class="safari-ui">
            <span class="mk mk-left"></span>
            <div class="center-div">
              <span class="shield"></span>
              <div class="address-bar"><span class="lock"></span><p>${url}</p></div>
              <span class="shield spacer"></span>
            </div>
            <span class="mk mk-right"></span>
          </div>
        </div>
        <div class="browser-viewport" style="aspect-ratio:${ar}">${media}</div>
      </div></div>`;
    }
    const cls = type === 'iphone' ? `phone is-iphone ${color}` : `phone ${color}`;
    return `
    <div class="mockup-export"><div class="${cls.trim()}" style="width:360px">
      <div class="device-asset"></div>
      <div class="drop-wrapper"><div class="dropzone">${media}</div></div>
    </div></div>`;
  }

  function fitPreview() {
    const node = hostRef.current?.firstElementChild as HTMLElement | null;
    const scale = scaleRef.current;
    const stage = stageRef.current;
    if (!node || !scale || !stage) return;
    scale.style.transform = 'scale(1)';
    const avail = Math.min(stage.clientWidth - 60, 1200);
    const w = node.getBoundingClientRect().width || 1;
    const s = Math.min(1, avail / w);
    previewScale.current = s;
    scale.style.transform = `scale(${s})`;
  }

  // Rebuild del preview con el CSS real ante cualquier cambio de estado.
  useLayoutEffect(() => {
    document.documentElement.dataset.browser = IS_BROWSER(type) ? type : 'chrome';
    document.documentElement.classList.toggle('is-dark', theme === 'dark');
    document.documentElement.classList.toggle('is-light', theme === 'light');
    if (hostRef.current) {
      hostRef.current.innerHTML = buildMarkup();
      const el = hostRef.current.querySelector('img, video') as HTMLElement | null;
      if (el) el.addEventListener('pointerdown', onPointerDown as EventListener);
    }
    fitPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, theme, color, url, aspect, width, img, mediaKind, bg]);

  // Listeners globales de arrastre (una vez).
  useEffect(() => {
    const move = (e: PointerEvent) => {
      const d = drag.current;
      if (!d) return;
      const dx = (e.clientX - d.x) / previewScale.current;
      const dy = (e.clientY - d.y) / previewScale.current;
      if (d.ovx > 0) posRef.current.x = clamp(d.px - (dx / d.ovx) * 100);
      if (d.ovy > 0) posRef.current.y = clamp(d.py - (dy / d.ovy) * 100);
      d.img.style.objectPosition = `${posRef.current.x}% ${posRef.current.y}%`;
    };
    const up = () => {
      if (drag.current) {
        drag.current.img.classList.remove('dragging');
        drag.current = null;
      }
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    const onResize = () => fitPreview();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('resize', onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onPointerDown(e: PointerEvent) {
    const el = e.currentTarget as HTMLImageElement | HTMLVideoElement;
    if (!img || !dimRef.current.w || !dimRef.current.h) return;
    e.preventDefault();
    const boxW = el.clientWidth;
    const boxH = el.clientHeight;
    const s = Math.max(boxW / dimRef.current.w, boxH / dimRef.current.h);
    drag.current = {
      x: e.clientX,
      y: e.clientY,
      px: posRef.current.x,
      py: posRef.current.y,
      ovx: Math.max(0, dimRef.current.w * s - boxW),
      ovy: Math.max(0, dimRef.current.h * s - boxH),
      img: el,
    };
    el.classList.add('dragging');
  }

  function loadFile(file?: File | null) {
    if (!file) return;
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    if (!isImage && !isVideo) return;
    fileRef.current = file;
    posRef.current = { x: 50, y: 0 };
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    if (isVideo) {
      const src = URL.createObjectURL(file);
      objectUrlRef.current = src;
      const v = document.createElement('video');
      v.onloadedmetadata = () => {
        dimRef.current = { w: v.videoWidth, h: v.videoHeight };
        setMediaKind('video');
        setImg(src);
      };
      v.src = src;
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const src = reader.result as string;
      const im = new Image();
      im.onload = () => {
        dimRef.current = { w: im.naturalWidth, h: im.naturalHeight };
        setMediaKind('image');
        setImg(src);
      };
      im.src = src;
      setMediaKind('image');
      setImg(src);
    };
    reader.readAsDataURL(file);
  }

  async function onExport() {
    const node = hostRef.current?.querySelector('.mockup-export') as HTMLElement | null;
    if (!node) return;
    setExporting(true);
    try {
      if (mediaKind === 'video' && fileRef.current) {
        setStatus('Preparando…');
        const { webm, mp4 } = await exportVideo(node, fileRef.current, {
          fps: 30,
          maxSeconds: 10,
          bg,
          onProgress: (m) => setStatus(m),
        });
        downloadBlob(webm, `mockup-${type}.webm`);
        downloadBlob(mp4, `mockup-${type}.mp4`);
        setStatus('WebM + MP4 descargados ✓');
        return;
      }
      setStatus('Componiendo…');
      const blob = await exportImagePng(node);
      downloadBlob(blob, `mockup-${type}.png`);
      setStatus('PNG descargado ✓');
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setStatus('Error: ' + (msg || 'fallo desconocido'));
    } finally {
      setExporting(false);
    }
  }

  const isBrowser = IS_BROWSER(type);
  const colorOpts = COLORS[type] || [];

  return (
    <div>
      <h2>Mockup Studio</h2>
      <p className="muted">Enmarca una captura en Chrome, Safari, iPhone o Pixel y expórtala a PNG transparente.</p>

      <div className="mk-studio">
        <aside className="mk-panel">
          <div className="mk-field">
            <label>Tipo</label>
            <div className="mk-seg">
              {(['chrome', 'safari', 'iphone', 'pixel'] as Kind[]).map((t) => (
                <button
                  key={t}
                  aria-pressed={type === t}
                  onClick={() => {
                    setType(t);
                    setColor('');
                  }}
                >
                  {t === 'pixel' ? 'Pixel 7' : t[0].toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {isBrowser && (
            <div className="mk-field">
              <label>Tema</label>
              <div className="mk-seg">
                {(['light', 'dark'] as const).map((t) => (
                  <button key={t} aria-pressed={theme === t} onClick={() => setTheme(t)}>
                    {t === 'light' ? 'Claro' : 'Oscuro'}
                  </button>
                ))}
              </div>
            </div>
          )}

          {!isBrowser && (
            <div className="mk-field">
              <label>Color</label>
              <div className="mk-seg">
                {colorOpts.map((o) => (
                  <button key={o.id} aria-pressed={color === o.id} onClick={() => setColor(o.id)}>
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {isBrowser && (
            <>
              <div className="mk-field">
                <label>URL</label>
                <input className="mk-input" value={url} onChange={(e) => setUrl(e.target.value || ' ')} />
              </div>
              <div className="mk-field">
                <label>Proporción de ventana</label>
                <div className="mk-seg">
                  {ASPECTS.map((a) => (
                    <button key={a} aria-pressed={aspect === a} onClick={() => setAspect(a)}>
                      {ASPECT_LABEL[a]}
                    </button>
                  ))}
                </div>
                <label style={{ marginTop: 14 }}>Ancho · {width} px</label>
                <input
                  type="range"
                  min={760}
                  max={1680}
                  step={20}
                  value={width}
                  style={{ width: '100%' }}
                  onChange={(e) => setWidth(Number(e.target.value))}
                />
              </div>
            </>
          )}

          {mediaKind === 'video' && (
            <div className="mk-field">
              <label>Fondo (MP4)</label>
              <input
                type="color"
                className="mk-input"
                value={bg}
                onChange={(e) => setBg(e.target.value)}
              />
            </div>
          )}

          <div className="mk-field">
            <label>Captura</label>
            <label
              className={'mk-drop' + (dragActive ? ' drag' : '')}
              onDragOver={(e) => {
                e.preventDefault();
                setDragActive(true);
              }}
              onDragLeave={() => setDragActive(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragActive(false);
                loadFile(e.dataTransfer.files[0]);
              }}
            >
              <strong>Sube tu captura</strong>
              <br />o arrástrala aquí
              <input type="file" accept="image/*,video/*" hidden onChange={(e) => loadFile(e.target.files?.[0])} />
            </label>
          </div>

          <button className="mk-export-btn" disabled={!img || exporting} onClick={onExport}>
            {mediaKind === 'video' && 'Exportar vídeo'}
            {mediaKind !== 'video' && 'Exportar PNG'}
          </button>
          <div className="mk-status">{status}</div>
        </aside>

        <main className="mk-stage" ref={stageRef}>
          <div className="mk-scale" ref={scaleRef}>
            <div ref={hostRef} />
          </div>
        </main>
      </div>
    </div>
  );
}
